import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import dayjs from "dayjs";
import { I18nProvider } from "@lingui/react";
import { loadCatalog } from "@app/i18n";
import type { AppLocale } from "@app/i18n";
import type { I18n } from "@lingui/core";

const LOCALE_STORAGE_KEY = "mage-x-locale";

interface LocaleContextValue {
  locale: AppLocale;
  setAppLocale: (locale: AppLocale) => Promise<void>;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  initialLocale,
  i18n,
  children,
}: {
  initialLocale: AppLocale;
  i18n: I18n;
  children: ReactNode;
}) {
  const [locale, setLocale] = useState<AppLocale>(initialLocale);

  const setAppLocale = useCallback(async (newLocale: AppLocale) => {
    await loadCatalog(newLocale);
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    dayjs.locale(newLocale);
    setLocale(newLocale);
  }, []);

  return (
    <LocaleContext.Provider value={{ locale, setAppLocale }}>
      <I18nProvider key={locale} i18n={i18n}>
        {children}
      </I18nProvider>
    </LocaleContext.Provider>
  );
}

export function useLocaleContext(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocaleContext must be used within LocaleProvider");
  }
  return ctx;
}
